import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import "jspdf-autotable";

/* ================= HELPERS ================= */

const HEADER_FILL = "FF9BBB59"; // Excel olive green header
const BORDER = { style: "thin", color: { argb: "FFBFBFBF" } };
const ALL_BORDERS = {
  top: BORDER,
  left: BORDER,
  bottom: BORDER,
  right: BORDER,
};

// "2026-01-02" -> Date at UTC midnight (ExcelJS writes dates as UTC)
const toExcelDate = (value) => (value ? new Date(value) : null);

// "2026-01-02" -> "1/2/2026" for the PDF, same as the Excel date format
const toText = (value) => {
  if (!value) return "";
  const [year, month, day] = String(value).slice(0, 10).split("-");
  return `${Number(month)}/${Number(day)}/${year}`;
};

const amountText = (value) =>
  Number(value || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

// One PDF row per collection, in COLUMNS order
const toTextRow = (item) => [
  toText(item.date),
  item.customer_name || "",
  item.account_name || "",
  item.check_number || "",
  toText(item.check_date),
  amountText(item.amount),
  item.status || "",
];

const COLUMNS = [
  { header: "DATE", width: 14, align: "center" },
  { header: "PAYEE NAME / CUSTOMER", width: 38, align: "left" },
  { header: "ACCOUNT NAME", width: 22, align: "center" },
  { header: "CHECK #", width: 18, align: "center" },
  { header: "CHECK DATE", width: 14, align: "center" },
  { header: "AMOUNT", width: 18, align: "right" },
  { header: "STATUS", width: 12, align: "center" },
];

/* ================= EXPORT ================= */

// Green header cell, same for both tables
const styleHeaderCell = (cell) => {
  cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
  cell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: HEADER_FILL },
  };
  cell.alignment = { horizontal: "left", vertical: "middle" };
  cell.border = ALL_BORDERS;
};

// Separate one-column Customer table, one empty column after the main table
const CUSTOMER_COLUMN = COLUMNS.length + 2;

// User Reports > Collection rows (as returned by /userReport/getCollectionReport)
// customers: every active customer's name, listed in the Customer table
export const exportUserCollectionReport = async (
  rows,
  fileName,
  customers = [],
) => {
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet("Collection", {
    views: [{ state: "frozen", ySplit: 1 }],
  });

  sheet.columns = [
    ...COLUMNS.map(({ width }) => ({ width })),
    { width: 4 }, // gap
    { width: 38 }, // Customer table
  ];

  const header = sheet.addRow(COLUMNS.map(({ header }) => header));
  header.height = 20;
  header.eachCell(styleHeaderCell);

  rows.forEach((item) => {
    const row = sheet.addRow([
      toExcelDate(item.date),
      item.customer_name || "",
      item.account_name || "",
      item.check_number || "",
      toExcelDate(item.check_date),
      Number(item.amount || 0),
      item.status || "",
    ]);

    row.getCell(1).numFmt = "m/d/yyyy";
    row.getCell(5).numFmt = "m/d/yyyy";
    row.getCell(6).numFmt = "#,##0.00";
    row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      cell.alignment = { horizontal: COLUMNS[colNumber - 1].align };
      cell.border = ALL_BORDERS;
    });
  });

  const customerHeader = sheet.getCell(1, CUSTOMER_COLUMN);
  customerHeader.value = "CUSTOMER";
  styleHeaderCell(customerHeader);
  // Black header so it reads as a separate table
  customerHeader.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FF000000" },
  };
  customers.forEach((name, index) => {
    const cell = sheet.getCell(index + 2, CUSTOMER_COLUMN);
    cell.value = name;
    cell.border = ALL_BORDERS;
  });

  // Filter dropdowns on every header cell of the main table
  sheet.autoFilter = {
    from: { row: 1, column: 1 },
    to: { row: 1, column: COLUMNS.length },
  };

  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(
    new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }),
    `${fileName}.xlsx`,
  );
};

const PDF_ROWS_PER_PAGE = 20;

// subtitle: user and date range, printed under the title
export const exportUserCollectionPDF = (rows, fileName, subtitle) => {
  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });

  const columnStyles = {};
  COLUMNS.forEach(({ align }, index) => {
    columnStyles[index] = { halign: align };
  });

  // At most 20 rows per page; title and header repeat on every page
  const pageCount = Math.max(1, Math.ceil(rows.length / PDF_ROWS_PER_PAGE));
  for (let page = 0; page < pageCount; page++) {
    if (page > 0) doc.addPage();

    doc.setFontSize(14);
    doc.setFont(undefined, "bold");
    doc.text("Collection Report", 40, 40);
    doc.setFontSize(10);
    doc.setFont(undefined, "normal");
    doc.text(subtitle, 40, 58);

    const start = page * PDF_ROWS_PER_PAGE;
    doc.autoTable({
      startY: 72,
      head: [COLUMNS.map(({ header }) => header)],
      body: rows.slice(start, start + PDF_ROWS_PER_PAGE).map(toTextRow),
      theme: "grid",
      styles: { fontSize: 9, cellPadding: 4, lineColor: [191, 191, 191] },
      headStyles: {
        fillColor: [155, 187, 89], // same green as the Excel header
        textColor: 255,
        fontStyle: "bold",
      },
      columnStyles,
      margin: { left: 40, right: 40 },
    });

    doc.setFontSize(9);
    doc.text(
      `Page ${page + 1} of ${pageCount}`,
      doc.internal.pageSize.getWidth() - 40,
      doc.internal.pageSize.getHeight() - 20,
      { align: "right" },
    );
  }

  doc.save(`${fileName}.pdf`);
};
